import { FloatingPanelDockZone } from '@shared/floating-panel/floating-panel.types';
import { HdBodygraphPlanetaryActivation } from '../hd-bodygraph.models';
import { HdGateInfoRequestedContext } from '../gate-info/hd-gate-info-requested-context.model';

/** Eén kanaal binnen het kanaal-info paneel (bij integratie kunnen dit er meerdere zijn). */
export interface HdChannelInfoChannelEntry {
  readonly channelKey: string;
  readonly gates: readonly [number, number];
}

/**
 * Context voor {@link HdChannelInfoRequestedComponent}. Gebouwd door de
 * editor-page bij een klik op een kanaalstuk met kanaal-highlight (spec
 * `2026-07-02-hd-kanaal-highlight-en-kanaal-info-design.md` §6.2).
 */
export interface HdChannelInfoRequestedContext {
  /** Discriminator t.o.v. de gate-info contexts. */
  readonly kind: 'channel-requested';
  /** ≥ 1 kanaal, canonieke HD_BODYGRAPH_CHANNELS-volgorde. */
  readonly channels: readonly HdChannelInfoChannelEntry[];
  /** Alle planeet-activaties van alle betrokken poorten in de huidige chart. */
  readonly activations: readonly HdBodygraphPlanetaryActivation[];
  /**
   * Weergavestijl van de transitlaag: 'just-now' (default) of 'cycle'
   * (cycle-rijlabel; zie {@link transitRowLabel}).
   */
  readonly transitStyle?: 'just-now' | 'cycle';
  /** Label voor transit-rijen; afwezig/null = het Just Now-label. */
  readonly transitRowLabel?: string | null;
  /**
   * Weergavestijl van de basislaag: 'standard' (default) of 'composite'
   * (chart 1 kleurt natal-blauw — twee personen, twee kleuren).
   */
  readonly baseStyle?: 'standard' | 'composite';
  /**
   * Naam vóór het kant-label van de basisrijen (composite: chart 1 — "Steven ·
   * Personality · South Node"), zoals de transit-rijen de partner-naam dragen.
   */
  readonly baseRowLabel?: string | null;
  /**
   * Per betrokken poort een voorgebouwde gate-info-context (via de bestaande
   * `buildChartGateContext` van de page) zodat de embedded poort-weergave
   * zelfstandig werkt — incl. terugknop-navigatie zonder page-callbacks.
   */
  readonly gateContexts: Readonly<Record<number, HdGateInfoRequestedContext>>;
  /**
   * `true` → paneel opent meteen in de brede weergave. Gekoppeld aan het
   * globale open-gedrag: de full/full-docked-* gedragingen openen breed,
   * net zoals poort-panelen daar meteen volledig openen.
   */
  readonly initialWide?: boolean;
  /**
   * `true` → een klik op een poort of op de volledige kanaaldocumentatie opent
   * het detail als een apart gedockt panel; het compacte kanaal-info paneel
   * blijft ongewijzigd staan. Gekoppeld aan de globale setting
   * `gateInfoFromChannelSeparateToDock`. Afwezig/`false` toont beide details
   * in-place met terugknoppen.
   */
  readonly gateClickOpensSeparateDock?: boolean;
  /**
   * Dock-zone waarin de apart geopende poort landt wanneer
   * {@link gateClickOpensSeparateDock} actief is. Volgt de dock-zone van de
   * pagina (`'small'` of `'full'`). Default `'small'`.
   */
  readonly separateDockZone?: FloatingPanelDockZone;
  /**
   * Maan-transit modus (spec `2026-07-06-hd-moon-transit-chart-design.md`
   * §6.6): deze poort heeft geen echte activatie maar een virtuele
   * maan-transit-activatie — de view-model rendert er een transit-rij voor
   * (☽-glyph, "Maan-transit"-label, geen lijnnummer).
   */
  readonly moonTransitGate?: number;
  /** Echte personality-planeetactivaties uit de actuele Just Now-transit. */
  readonly justNowTransitActivations?: readonly HdBodygraphPlanetaryActivation[];
}

/** Panel-key: `channel-info:<keys '+'-gescheiden>` (dedupe per cluster). */
export function buildHdChannelInfoPanelKey(channelKeys: readonly string[]): string {
  return `channel-info:${channelKeys.join('+')}`;
}

/** Docktab/terugknop-notatie: "34-57", of "34-20 +2" bij een cluster. */
export function buildHdChannelInfoDockLabel(channelKeys: readonly string[]): string {
  if (channelKeys.length <= 1) {
    return channelKeys[0] ?? '';
  }
  return `${channelKeys[0]} +${channelKeys.length - 1}`;
}
